import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import SignUpForm from '../components/auth/SignUpForm';
import VerificationForm from '../components/auth/VerificationForm';

type AuthMode = 'login' | 'signup' | 'verify';
type VerificationPath = 'email' | 'phone' | 'both';

const AuthPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<AuthMode>('login');
  const [identifier, setIdentifier] = useState('');
  const [verificationPath, setVerificationPath] = useState<VerificationPath>('email');
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/events" replace />;

  const handleSignUpSuccess = (
    id: string,
    _username: string,
    path: VerificationPath,
    phone: string,
    password: string,
  ) => {
    setIdentifier(id);
    setVerificationPath(path);
    setVerifyPhone(phone);
    setVerifyPassword(password);
    setMode('verify');
  };

  const handleVerificationSuccess = () => {
    setVerifyPassword('');
    setMode('login');
  };

  const handleLoginSuccess = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-600 mb-2">🧘‍♀️ Pop-Up Yoga</h1>
          <p className="text-gray-600">Find your balance in beautiful outdoor locations</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {mode === 'login' && (
          <LoginForm onSuccess={handleLoginSuccess} onSwitchToSignUp={() => setMode('signup')} />
        )}

        {mode === 'signup' && (
          <SignUpForm onSuccess={handleSignUpSuccess} onSwitchToLogin={() => setMode('login')} />
        )}

        {mode === 'verify' && (
          <VerificationForm
            identifier={identifier}
            verificationPath={verificationPath}
            phone={verifyPhone}
            password={verifyPassword}
            onSuccess={handleVerificationSuccess}
            onBack={() => setMode('signup')}
          />
        )}
      </div>

      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
